import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import TomAssistant from './TomAssistant'
import TomTextAssistant from './TomTextAssistant'
import { useTomAssistantMode } from '../hooks/useTomAssistantMode'
import HeaderDisplayControls from './HeaderDisplayControls'
import {
  Home,
  Users,
  Calendar,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  Smartphone,
  GraduationCap,
  BarChart3,
  LayoutGrid,
  CreditCard,
  Shield,
  Clock,
  Mail,
  UserCheck,
  School,
  Cpu,
  Target,
  CalendarDays,
  FolderOpen,
  Folder,
  ChevronDown,
  Layers,
  LayoutTemplate,
  Monitor,
  MoreHorizontal,
  ShieldAlert,
  Wallet,
  Mic2
} from 'lucide-react'
import logo from '../assets/logo.png'
import logoNegative from '../assets/logo-negative.png'
import { getTomRouteLabel } from '../lib/tomRouteContext'
import { api } from '../lib/api'
import {
  canAccessMenuKey,
  isAdminUser,
  NAVIGATION_PERMISSION_UPDATED_EVENT,
  NavigationMenuKey,
  NavigationPermissionMatrix,
  readMatrixFromStorage,
  sanitizeNavigationPermissionMatrix,
  writeMatrixToStorage
} from '../lib/navigationPolicy'

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  menuKey: NavigationMenuKey
  name: string
  href: string
  icon: React.ElementType
  disabled?: boolean
}

interface NavGroup {
  menuKey?: NavigationMenuKey
  label: string | null
  icon?: React.ElementType
  href?: string
  items: NavItem[]
  studentOnly?: boolean
}

const navigationGroups: NavGroup[] = [
  {
    menuKey: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    href: '/dashboard',
    items: [],
  },
  {
    label: 'Unterricht',
    icon: Layers,
    items: [
      { menuKey: 'unterricht.lernbuero', name: 'Lernbüro', href: '/lernbuero', icon: GraduationCap },
      { menuKey: 'unterricht.unterrichten', name: 'Unterrichten', href: '/live-unterricht', icon: School },
      { menuKey: 'unterricht.klassenbuch', name: 'Klassenbuch', href: '/klassenbuch', icon: BookOpen },
      { menuKey: 'unterricht.klassenarbeit', name: 'Klassenarbeit', href: '/unterrichten/klassenarbeit', icon: BookOpen },
      { menuKey: 'unterricht.display', name: 'Display', href: '/display-steuerung', icon: Monitor },
      { menuKey: 'unterricht.anwesenheit', name: 'Anwesenheit', href: '/attendance', icon: Calendar },
      { menuKey: 'unterricht.sitzordnung', name: 'Sitzordnung', href: '/sitzordnung', icon: LayoutGrid },
    ]
  },
  {
    label: 'Schüler',
    icon: Users,
    items: [
      { menuKey: 'schueler.verwaltung', name: 'Schüler (alt)', href: '/students', icon: Users, disabled: true },
      { menuKey: 'schueler.verwaltung', name: 'Schüler', href: '/students-v2', icon: Users },
      { menuKey: 'schueler.statistik', name: 'Statistik', href: '/statistics', icon: BarChart3 },
      { menuKey: 'schueler.lernstand_automatik', name: 'Lernstand-Automatik', href: '/student-assessments', icon: ShieldAlert },
      { menuKey: 'schueler.akten', name: 'Akten', href: '/Akten', icon: FolderOpen },
      { menuKey: 'schueler.lerngruppen', name: 'Lerngruppen', href: '/units', icon: School },
    ]
  },
  {
    menuKey: 'email.client',
    label: 'E-Mail',
    icon: Mail,
    href: '/E-Mail',
    items: [],
  },
  {
    menuKey: 'dateien',
    label: 'Dateien',
    icon: Folder,
    href: '/dateien',
    items: [],
  },
  {
    label: 'Sonstiges',
    icon: MoreHorizontal,
    items: [
      { menuKey: 'sonstiges.bausteine', name: 'Bausteine', href: '/building-blocks', icon: BookOpen },
      { menuKey: 'sonstiges.namen_lernen', name: 'Namen lernen', href: '/namen-lernen', icon: UserCheck },
      { menuKey: 'teacher.appointments', name: 'Terminbuchungen', href: '/appointments', icon: CalendarDays },
      { menuKey: 'sonstiges.zahlungen', name: 'Zahlungen', href: '/payments', icon: CreditCard },
    ]
  },
  {
    menuKey: 'settings',
    label: 'Einstellungen',
    icon: Settings,
    href: '/settings',
    items: [],
  },
  {
    label: 'Admin',
    icon: ShieldAlert,
    items: [
      { menuKey: 'admin.curricula', name: 'Curricula', href: '/curricula', icon: BookOpen },
      { menuKey: 'admin.geraetemanager', name: 'Gerätemanager', href: '/device-manager', icon: Smartphone },
      { menuKey: 'admin.job_queue', name: 'Job-Queue', href: '/admin/job-queue', icon: Clock },
      { menuKey: 'admin.kontostand', name: 'Rechnungsübersicht', href: '/admin/kontostand', icon: Wallet },
      { menuKey: 'admin.performance', name: 'Performance', href: '/performance', icon: Cpu },
      { menuKey: 'admin.stundenplan', name: 'Stundenpläne', href: '/timetables', icon: Calendar },
      { menuKey: 'admin.showcase', name: 'UI Showcase', href: '/Showcase', icon: LayoutTemplate },
      { menuKey: 'admin.license', name: 'Lizenz', href: '/license', icon: Shield },
      {
        menuKey: 'admin.acoustic_calibration',
        name: 'Audio-Kalibrierung',
        href: '/admin/kalibrierung',
        icon: Mic2
      },
    ]
  },
  {
    label: null,
    studentOnly: true,
    items: [
      { menuKey: 'student.dashboard', name: 'Schüler Dashboard', href: '/Schüler_Dashboard', icon: Home },
      { menuKey: 'student.lernplaene', name: 'Meine Lernpläne', href: '/Meine Lernpläne', icon: BookOpen },
      { menuKey: 'student.skillmap', name: 'Skillmap', href: '/Skillmap', icon: Target },
      { menuKey: 'student.classwork', name: 'Klassenarbeit', href: '/student/classwork', icon: BookOpen },
      { menuKey: 'student.klassenbuch', name: 'Mein Klassenbuch', href: '/student/klassenbuch', icon: BookOpen },
      { menuKey: 'student.dateien', name: 'Dateien', href: '/dateien', icon: Folder },
      { menuKey: 'student.appointments', name: 'Terminbuchungen', href: '/appointments/student', icon: CalendarDays },
    ]
  },
]


function TomAssistantHeader({ currentContext }: { currentContext: string }) {
  const { isTextMode, loading } = useTomAssistantMode()
  if (loading) return null
  return (
    <div className="flex items-center">
      {isTextMode ? (
        <TomTextAssistant currentContext={currentContext} />
      ) : (
        <TomAssistant currentContext={currentContext} />
      )}
    </div>
  )
}

const SidebarToggleIcon = ({ collapsed }: { collapsed: boolean }) => (
  <span className="relative block h-5 w-5 text-gray-500 dark:text-gray-400">
    <span className="absolute inset-0 block rounded-md border border-current opacity-80" />
    <span
      className={`absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-sm bg-current opacity-80 transition-all duration-200 ${
        collapsed ? 'right-1.5' : 'left-1.5'
      }`}
    />
  </span>
)

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })
  const [tomContext, setTomContext] = useState<string>('')
  const location = useLocation()

  // Accordion: nur eine Kategorie gleichzeitig offen, ruhigeres Menü
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null)
  const [sidebarHovered, setSidebarHovered] = useState(false)

  const toggleGroup = (label: string) => {
    setExpandedGroup(prev => (prev === label ? null : label))
  }
  const { user, logout } = useAuth()
  const isAdmin = isAdminUser(user)
  const [permissionMatrix, setPermissionMatrix] = useState<NavigationPermissionMatrix>(() => readMatrixFromStorage())
  const isSidebarCompact = sidebarCollapsed && !sidebarHovered
  const showSidebarToggle = !isSidebarCompact
  /** Volle Content-Breite wie bei Unterrichten (kein max-w-7xl) */
  const isFullWidthMain =
    location.pathname === '/unterrichten'
    || location.pathname === '/live-unterricht'
    || location.pathname === '/student-assessments'
    || location.pathname === '/E-Mail'
    || location.pathname === '/email-client'
    || location.pathname === '/students-v2'
    || location.pathname === '/dashboard'
    || location.pathname === '/building-blocks'

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  useEffect(() => {
    let mounted = true
    const loadNavigationPermissions = async () => {
      try {
        const res = await api.get('/settings/navigation-permissions')
        if (!mounted || !res?.data?.success) return
        const matrix = sanitizeNavigationPermissionMatrix((res.data.data?.matrix || {}) as NavigationPermissionMatrix)
        setPermissionMatrix(matrix)
        writeMatrixToStorage(matrix)
      } catch {
        // keep local fallback
      }
    }
    if (user) {
      void loadNavigationPermissions()
    }
    return () => {
      mounted = false
    }
  }, [user?.username])

  useEffect(() => {
    const syncMatrixFromStorage = () => {
      setPermissionMatrix(readMatrixFromStorage())
    }

    const handleCustomMatrixUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ matrix?: NavigationPermissionMatrix }>
      if (customEvent.detail?.matrix) {
        setPermissionMatrix(customEvent.detail.matrix)
        return
      }
      syncMatrixFromStorage()
    }

    window.addEventListener('storage', syncMatrixFromStorage)
    window.addEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, handleCustomMatrixUpdate as EventListener)
    return () => {
      window.removeEventListener('storage', syncMatrixFromStorage)
      window.removeEventListener(NAVIGATION_PERMISSION_UPDATED_EVENT, handleCustomMatrixUpdate as EventListener)
    }
  }, [])

  const isItemAllowed = (menuKey: NavigationMenuKey): boolean => {
    return canAccessMenuKey({
      menuKey,
      user,
      matrix: permissionMatrix
    })
  }

  const studentMenuKeys: NavigationMenuKey[] = [
    'student.dashboard',
    'student.lernplaene',
    'student.skillmap',
    'student.classwork',
    'student.klassenbuch',
    'student.dateien',
    'student.appointments'
  ]

  const getFilteredGroups = () => {
    return navigationGroups
      .filter(group => {
        if (!group.studentOnly) return true
        return studentMenuKeys.some((menuKey) => isItemAllowed(menuKey))
      })
      .map(group => ({
        ...group,
        items: group.items.filter(item => isItemAllowed(item.menuKey))
      }))
      .filter(group => {
        // Direct-link groups (href + no items): check the href itself
        if (group.menuKey && group.href && group.items.length === 0) return isItemAllowed(group.menuKey)
        return group.items.length > 0
      })
  }

  // Legacy flat list (used nowhere directly anymore, kept for safety)
  const getFilteredNavigation = () => getFilteredGroups().flatMap(g => g.items)

  // Kontext basierend auf aktueller Seite setzen (Pfad + ggf. Suchparameter)
  useEffect(() => {
    const context = getContextForPage(location.pathname, location.search)
    setTomContext(context)
  }, [location.pathname, location.search])

  // Accordion: Gruppe mit aktiver Seite automatisch öffnen
  useEffect(() => {
    const groups = getFilteredGroups()
    const group = groups.find(g => g.label && g.items.some(item => item.href === location.pathname))
    setExpandedGroup(prev => (group?.label ?? null))
  }, [location.pathname])

  const getContextForPage = (pathname: string, search: string): string => {
    const queryHint =
      search && search.length > 1
        ? ` Zusätzliche URL-Parameter: ${search}.`
        : ''
    const navTail = ` Aktueller Menü-/Seitenname: „${getTomRouteLabel(pathname)}“. Technischer URL-Pfad: ${pathname}.`

    let base: string
    switch (pathname) {
      case '/Schüler_Dashboard':
        base = 'Der Schüler sieht seinen persönlichen Fortschritt sowie die aktuelle Platzierung innerhalb der Klasse.'
        break
      case '/Meine Lernpläne':
        base = 'Der Schüler betrachtet alle derzeit aktiven Lernpläne inklusive Laufzeit und Differenzierung.'
        break
      case '/Skillmap':
        base = 'Der Schüler navigiert durch seine Skillmap und erkennt, welche Kompetenzen bereits sicher beherrscht werden.'
        break
      case '/student/classwork':
        base = 'Der Schüler sieht zugewiesene Klassenarbeiten, startet sie und gibt Lösungen fristgerecht ab.'
        break
      case '/dashboard':
        base = 'Der Benutzer ist auf dem Dashboard und kann eine Übersicht über alle wichtigen Informationen sehen.'
        break
      case '/students':
        base = 'Der Benutzer verwaltet Schüler und deren Daten. Hier können Schüler hinzugefügt, bearbeitet und verwaltet werden.'
        break
      case '/students-v2':
        base =
          'Der Benutzer nutzt die neue Schüler-Verwaltung (V2). Funktionsumfang wie die klassische Schüler-Seite; Layout und Struktur werden schrittweise überarbeitet.'
        break
      case '/attendance':
        base = 'Der Benutzer führt Anwesenheitskontrolle durch. Hier können Schüler als anwesend, krank oder im Lernbüro markiert werden.'
        break
      case '/learning-plans':
        base = 'Der Benutzer arbeitet mit Lernplänen. Hier können Lernpläne erstellt, bearbeitet und verwaltet werden.'
        break
      case '/building-blocks':
        base = 'Der Benutzer arbeitet mit Bausteinen. Hier können Unterrichtsbausteine erstellt und organisiert werden.'
        break
      case '/tasks':
        base = 'Der Benutzer arbeitet mit Aufgaben. Hier können Aufgaben zu Lernplänen hinzugefügt und verwaltet werden.'
        break
      case '/lernbuero':
        base = 'Der Benutzer ist im Lernbüro-Modus. Hier können Sitzungen erstellt und Schüler beim Lernen unterstützt werden.'
        break
      case '/unterrichten':
        base = 'Der Benutzer plant oder dokumentiert eine Unterrichtseinheit. Kompetenzen, Sitzpläne und Anwesenheit werden hier verwaltet.'
        break
      case '/live-unterricht':
        base = 'Der Benutzer steuert den Live-Unterricht mit Sitzordnung, Phasen, Timer und Tafelansicht.'
        break
      case '/units':
        base = 'Der Benutzer verwaltet Lerngruppen – Klassen, Kurse und Gruppen sowie deren Mitgliedschaften.'
        break
      case '/statistics':
        base = 'Der Benutzer betrachtet Statistiken und Auswertungen der Lernfortschritte.'
        break
      case '/student-assessments':
        base = 'Der Benutzer steuert die Lernstand-Automatik, löst manuelle Läufe aus und überwacht den Verlauf.'
        break
      case '/performance':
        base = 'Der Benutzer überwacht die Server-Performance, Rate Limits und erkennt Anomalien.'
        break
      case '/admin/kontostand':
        base =
          'Administration oder Schulleitung sieht die monatliche Nutzung der Assistenzsysteme mit Ihrem Rechnungsbetrag, aufgeschlüsselt nach Bereichen und Nutzerinnen und Nutzern.'
        break
      case '/admin/job-queue':
        base =
          'Die Administration sieht alle Hintergrund-Jobs (Importe, Klassenarbeiten, Redis-Queue) und kann wartende oder laufende Aufträge killen.'
        break
      case '/job-queue':
        base =
          'Der Benutzer sieht die eigene Job-Queue und kann eigene wartende oder laufende Hintergrundaufträge abbrechen.'
        break
      case '/sitzordnung':
        base = 'Der Benutzer erstellt und verwaltet die Sitzordnung der Klasse. Hier können Schüler in einem 8x8 Grid angeordnet werden.'
        break
      case '/device-manager':
        base = 'Der Benutzer verwaltet NFC-Geräte und deren Verbindungen.'
        break
      case '/payments':
        base = 'Der Benutzer verwaltet Geldkonten (PayPal, SEPA, Giro-FinTS) und erstellt Zahlungsaufforderungen.'
        break
      case '/license':
        base = 'Der Benutzer verwaltet die Software-Lizenzen und deren Konfiguration.'
        break
      case '/settings':
        base = 'Der Benutzer ist in den Einstellungen und kann die Software konfigurieren.'
        break
      case '/namen-lernen':
        base = 'Der Benutzer übt Namen und kann zwischen verschiedenen spielerischen Modi wählen.'
        break
      case '/elternbriefe':
      case '/massenmail':
        base = 'Der Benutzer verwaltet Elternbriefe und Massenmails mit Funktionen wie Lesebestätigungen und Umfragen.'
        break
      case '/appointments':
        base = 'Der Benutzer verwaltet Terminbuchungen für Elternsprechtage und Sprechstunden. Hier können Zeitfenster erstellt und Links an Eltern versendet werden.'
        break
      case '/appointments/all':
        base = 'Der Benutzer sieht eine Übersicht aller verfügbaren Terminbuchungen und kann einen Termin buchen.'
        break
      case '/dateien':
        base = 'Der Benutzer verwaltet persönliche Dateien zur Unterrichtsvorbereitung: Ordner, geteilte Dateien und Gruppenordner.'
        break
      case '/E-Mail':
      case '/email-client':
        base = 'Der Benutzer ist im integrierten E-Mail-Client (Posteingang, Entwürfe, ggf. Elternbriefe).'
        break
      case '/Akten':
        base = 'Der Benutzer arbeitet mit digitalen Schülerakten (Dokumente, Freigaben).'
        break
      case '/Akten-Prüfung':
        base = 'Der Benutzer prüft eingereichte Akten-Dokumente zur Freigabe.'
        break
      case '/unterrichten/klassenarbeit':
        base = 'Der Benutzer verwaltet Klassenarbeiten (Aufgaben, Abgaben, Bewertung).'
        break
      case '/display-steuerung':
        base = 'Der Benutzer verbindet die Anwendung mit dem Klassenraum-Display (Beamer/Fernseher).'
        break
      case '/curricula':
        base = 'Der Benutzer bearbeitet Curricula / Rahmenpläne.'
        break
      case '/appointments/student':
        base = 'Der Schüler sieht seine Terminbuchungen.'
        break
      default:
        base = 'Der Benutzer arbeitet in der Lernrakete-Software.'
    }

    return `${base}${queryHint}${navTail}`
  }

  const navActiveClass =
    'bg-primary-100 text-primary-900 dark:bg-primary-900/35 dark:text-primary-50'
  const navInactiveClass =
    'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-50'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 dark:bg-black/55"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-900">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <img src={logo} alt="Lernrakete Logo" className="h-8 w-auto dark:hidden" />
              <img
                src={logoNegative}
                alt="Lernrakete Logo"
                className="hidden h-12 w-auto dark:block"
              />
              <span className="ml-2 text-xl font-bold dark:text-white">Lernrakete</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {getFilteredGroups().map((group, groupIdx) => {
              const GroupIcon = group.icon
              const isDirect = !!group.href && group.items.length === 0
              const isActive = isDirect && location.pathname === group.href
              const isGroupCollapsed = !isDirect && group.label ? expandedGroup !== group.label : false
              const isHovered = !isDirect && group.label ? hoveredGroup === group.label : false
              const hasActiveItem = !isDirect && group.items.some(item => location.pathname === item.href)
              const showItems = !isDirect && expandedGroup === group.label

              const rowBase = 'w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm font-medium transition-all duration-200'
              return (
                <div
                  key={groupIdx}
                  className={groupIdx > 0 ? 'pt-1' : ''}
                  onMouseEnter={() => !isDirect && group.label && setHoveredGroup(group.label)}
                  onMouseLeave={() => !isDirect && group.label && setHoveredGroup(null)}
                >
                  {isDirect && (
                    <Link
                      to={group.href!}
                      className={`${rowBase} ${isActive ? navActiveClass : navInactiveClass}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {GroupIcon && <GroupIcon className="h-5 w-5 flex-shrink-0" />}
                      <span>{group.label}</span>
                    </Link>
                  )}
                  {!isDirect && (
                    <button
                      onClick={() => toggleGroup(group.label!)}
                      className={`${rowBase} ${hasActiveItem ? 'text-gray-700 dark:text-gray-200' : navInactiveClass}`}
                    >
                      {GroupIcon && <GroupIcon className="h-5 w-5 flex-shrink-0" />}
                      <span className="flex-1 text-left text-xs font-semibold uppercase tracking-wider">
                        {group.label}
                      </span>
                      <ChevronDown
                        className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${isGroupCollapsed && !isHovered ? '-rotate-90' : ''}`}
                      />
                    </button>
                  )}
                  {showItems && (
                    <div className="mt-0.5 space-y-0.5 pl-3 border-l border-gray-100 ml-3 dark:border-gray-700">
                      {group.items.map((item) => {
                        const itemActive = location.pathname === item.href
                        const disabledClass = 'cursor-not-allowed opacity-50 text-gray-400 dark:text-gray-500'
                        return (
                          item.disabled ? (
                            <div
                              key={item.href}
                              className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md ${disabledClass}`}
                              title="Deaktiviert"
                              aria-disabled="true"
                            >
                              <item.icon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                              {item.name}
                            </div>
                          ) : (
                            <Link
                              key={item.href}
                              to={item.href}
                              className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md ${
                                itemActive ? navActiveClass : navInactiveClass
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon className="mr-2.5 h-4 w-4 flex-shrink-0" />
                              {item.name}
                            </Link>
                          )
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
          <div className="border-t border-gray-200 p-4 dark:border-gray-700">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center dark:bg-primary-900/40">
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-200">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{user?.username}</p>
                {user?.role && (
                  <p className="text-xs text-gray-500 capitalize dark:text-gray-400">
                    {user.role === 'klassenlehrer' ? 'Klassenlehrer' :
                     user.role === 'abteilungsleiter' ? 'Abteilungsleiter' :
                     user.role === 'schulleitung' ? 'Schulleitung' :
                     user.role === 'admin' ? 'Administrator' :
                     user.role === 'teacher' ? 'Lehrer' :
                     user.role === 'student' ? 'Schüler' :
                     user.role}
                  </p>
                )}
                <button
                  onClick={logout}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center mt-1"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div 
        className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ease-in-out ${
          isSidebarCompact ? 'lg:w-16' : 'lg:w-64'
        }`}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 relative">
          {/* Toggle Button */}
          {showSidebarToggle && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="absolute -right-3 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:shadow-none"
              aria-label={sidebarCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
            >
              <SidebarToggleIcon collapsed={sidebarCollapsed} />
            </button>
          )}

          {/* Header */}
          <div className={`flex h-16 items-center ${isSidebarCompact ? 'justify-center px-2' : 'px-4'}`}>
            <img src={logo} alt="Lernrakete Logo" className="h-8 w-auto dark:hidden" />
            <img
              src={logoNegative}
              alt="Lernrakete Logo"
              className="hidden h-12 w-auto dark:block"
            />
            {(!sidebarCollapsed || sidebarHovered) && (
              <span className="ml-2 text-xl font-bold transition-opacity duration-200 dark:text-white">
                Lernrakete
              </span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {getFilteredGroups().map((group, groupIdx) => {
              const GroupIcon = group.icon
              const isDirect = !!group.href && group.items.length === 0
              const isActive = isDirect && location.pathname === group.href
              const isGroupCollapsed = !isDirect && group.label ? expandedGroup !== group.label : false
              const isHovered = !isDirect && group.label ? hoveredGroup === group.label : false
              const hasActiveItem = !isDirect && group.items.some(item => location.pathname === item.href)
              const showItems = !isDirect && (
                isSidebarCompact ? isHovered : expandedGroup === group.label
              )

              const rowBase = 'w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm font-medium transition-all duration-200 relative'
              return (
                <div
                  key={groupIdx}
                  className={`relative ${groupIdx > 0 ? 'pt-1' : ''}`}
                  onMouseEnter={() => !isDirect && group.label && setHoveredGroup(group.label)}
                  onMouseLeave={() => !isDirect && group.label && setHoveredGroup(null)}
                >
                  {/* Direct link (Dashboard, E-Mail, Einstellungen) */}
                  {isDirect && !isSidebarCompact && (
                    <Link
                      to={group.href!}
                      className={`${rowBase} ${isActive ? navActiveClass : navInactiveClass}`}
                    >
                      {GroupIcon && <GroupIcon className="h-5 w-5 flex-shrink-0" />}
                      <span>{group.label}</span>
                    </Link>
                  )}
                  {isDirect && isSidebarCompact && (
                    <Link
                      to={group.href!}
                      className={`${rowBase} justify-center ${isActive ? navActiveClass : navInactiveClass}`}
                      title={group.label ?? undefined}
                    >
                      {GroupIcon && <GroupIcon className="h-5 w-5 flex-shrink-0" />}
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        {group.label}
                      </div>
                    </Link>
                  )}

                  {/* Group with children – expanded sidebar */}
                  {!isDirect && !isSidebarCompact && (
                    <button
                      onClick={() => toggleGroup(group.label!)}
                      className={`${rowBase} ${hasActiveItem ? 'text-gray-700 dark:text-gray-200' : navInactiveClass}`}
                    >
                      {GroupIcon && <GroupIcon className="h-5 w-5 flex-shrink-0" />}
                      <span className="flex-1 text-left text-xs font-semibold uppercase tracking-wider">
                        {group.label}
                      </span>
                      <ChevronDown
                        className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${isGroupCollapsed && !isHovered ? '-rotate-90' : ''}`}
                      />
                    </button>
                  )}
                  {/* Group with children – compact sidebar: show icon only */}
                  {!isDirect && isSidebarCompact && GroupIcon && (
                    <div className={`group relative flex justify-center px-2 py-2 rounded-md cursor-pointer ${hasActiveItem ? navActiveClass : navInactiveClass}`}>
                      <GroupIcon className="h-5 w-5 flex-shrink-0" />
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        {group.label}
                      </div>
                    </div>
                  )}

                  {showItems && !isSidebarCompact && (
                    <div className="mt-0.5 space-y-0.5 pl-3 border-l border-gray-100 ml-3 dark:border-gray-700">
                      {group.items.map((item) => {
                        const itemActive = location.pathname === item.href
                        const disabledClass = 'cursor-not-allowed opacity-50 text-gray-400 dark:text-gray-500'
                        return (
                          item.disabled ? (
                            <div
                              key={item.href}
                              className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${disabledClass}`}
                              title="Deaktiviert"
                              aria-disabled="true"
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0 mr-2.5" />
                              <span>{item.name}</span>
                            </div>
                          ) : (
                            <Link
                              key={item.href}
                              to={item.href}
                              className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                                itemActive ? navActiveClass : navInactiveClass
                              }`}
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0 mr-2.5" />
                              <span>{item.name}</span>
                            </Link>
                          )
                        )
                      })}
                    </div>
                  )}
                  {showItems && isSidebarCompact && (
                    <div className="absolute left-full top-0 ml-1 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 py-1">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 mb-1">
                        {group.label}
                      </div>
                      {group.items.map((item) => {
                        const itemActive = location.pathname === item.href
                        const disabledClass = 'cursor-not-allowed opacity-50 text-gray-400 dark:text-gray-500'
                        return (
                          item.disabled ? (
                            <div
                              key={item.href}
                              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${disabledClass}`}
                              title="Deaktiviert"
                              aria-disabled="true"
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0 mr-2.5" />
                              <span>{item.name}</span>
                            </div>
                          ) : (
                            <Link
                              key={item.href}
                              to={item.href}
                              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                                itemActive
                                  ? 'text-primary-900 bg-primary-50 dark:text-primary-50 dark:bg-primary-900/35'
                                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'
                              }`}
                            >
                              <item.icon className="h-4 w-4 flex-shrink-0 mr-2.5" />
                              <span>{item.name}</span>
                            </Link>
                          )
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>

          {/* User Section – immer unten sichtbar wenn Sidebar ausgeklappt, ausgeblendet bei kollabiert */}
          <div
            className={`flex-shrink-0 border-t border-gray-200 dark:border-gray-700 ${!isSidebarCompact ? 'p-3' : 'p-2 flex justify-center'}`}
          >
            {!isSidebarCompact ? (
              <div className="rounded-b-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-700 border-t-0 p-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center dark:bg-primary-900/40">
                      <span className="text-sm font-semibold text-primary-700 dark:text-primary-200">
                        {user?.username?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{user?.username}</p>
                    {user?.role && (
                      <p className="text-xs text-gray-500 capitalize dark:text-gray-400">
                        {user.role === 'klassenlehrer' ? 'Klassenlehrer' :
                         user.role === 'abteilungsleiter' ? 'Abteilungsleiter' :
                         user.role === 'schulleitung' ? 'Schulleitung' :
                         user.role === 'admin' ? 'Administrator' :
                         user.role === 'teacher' ? 'Lehrer' :
                         user.role === 'student' ? 'Schüler' :
                         user.role}
                      </p>
                    )}
                    <button
                      onClick={logout}
                      className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1 mt-1.5"
                    >
                      <LogOut className="h-3.5 w-3.5 flex-shrink-0" />
                      Abmelden
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0"
                title={user?.username}
              >
                <span className="text-sm font-medium text-primary-700 dark:text-primary-200">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content – feste Breite damit kein Overflow (Sidebar ist fixed) */}
      <div
        className={`transition-all duration-300 ease-in-out min-w-0 ${
          isSidebarCompact ? 'lg:ml-16 lg:w-[calc(100vw-4rem)]' : 'lg:ml-64 lg:w-[calc(100vw-16rem)]'
        }`}
      >
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 shadow-sm dark:shadow-black/20 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-200 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1 min-w-0" />
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-2 sm:gap-x-3">
              <HeaderDisplayControls />
              <div
                className="hidden h-8 w-px shrink-0 bg-gray-200 sm:block dark:bg-gray-600"
                aria-hidden
              />
              <TomAssistantHeader currentContext={tomContext} />
            </div>
          </div>
        </div>

        <main className="py-6">
          <div
            className={
              isFullWidthMain
                ? 'mx-auto max-w-none px-2 sm:px-3 lg:px-4'
                : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'
            }
          >
            {children}
          </div>
        </main>
      </div>

    </div>
  )
}
